import React from 'react';

import challengeSettings from '../../../settingsParams';
import { isOngoing } from '../../../../../selectors/challenges';
import groupBy from 'lodash/groupBy';
import { toParticipantSteps } from '../../../../../contexts/ParticipantStepsDataProvider/toParticipantSteps';
import format from 'date-fns/format';
import flatten from 'lodash/flatten';
import {
  getRightDateFromBackend,
  getRightDateFromBackendDateTime,
} from '../../../../../selectors/dates';

import { useEnvironment } from '@wix/yoshi-flow-editor';
import { useChallengeData } from '../../../../../contexts/ChallengeDataProvider/ChallengeDataContext';
import { useSettings } from '@wix/tpa-settings/react';
import { TextAlignment } from '../../../Settings/challengeSettings/challengeSettings.types';

import { ChallengeAgendaStepsList } from './components/ChallengeAgendaStepsList';
import { ChallengeAgendaSection } from './components/ChallengeAgendaSection';

import { classes, st } from './ChallengeAgenda.st.css';
import { DateFnsLocales } from '../../../../../services/DateFnsLocales';
import { isSpecific } from '../../../../../selectors/isSpecific';
import { isSelfPaced } from '../../../../../selectors/isSelfPaced';
import { FCWithChildren } from '@wix/challenges-web-library';

export interface IChallengeAgendaProps {
  className?: string;
  alignment?: TextAlignment;
}

const isLimitReached = (originalSteps, originalSections, limit) => {
  return (
    originalSteps?.length > limit ||
    flatten(originalSections?.map(({ steps = [] }) => steps)).length > limit
  );
};

const getStepsWithLimit = (isOpened, originalSteps, limit) => {
  // eslint-disable-next-line no-nested-ternary
  return originalSteps
    ? isOpened
      ? originalSteps
      : originalSteps?.slice(0, limit)
    : null;
};

const getSectionsWithLimit = (
  isOpened,
  originalSections,
  limit,
  isFirstPageLimitReached,
) => {
  if (!originalSections) {
    return null;
  }
  if (isOpened || !isFirstPageLimitReached) {
    return originalSections;
  }

  return originalSections?.reduce(
    (acc, section) => {
      const { sections, stepCount } = acc;
      if (stepCount < limit) {
        return {
          stepCount: Math.min(stepCount + section?.steps?.length, limit),
          sections: [
            ...sections,
            {
              ...section,
              steps: section?.steps?.slice(0, limit - stepCount),
            },
          ],
        };
      }
      return acc;
    },
    { sections: [], stepCount: 0 },
  ).sections;
};

export const ChallengeAgenda: FCWithChildren<IChallengeAgendaProps> = ({
  className = null,
  alignment = TextAlignment.Left,
}: IChallengeAgendaProps) => {
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const [opened, setOpened] = React.useState<boolean>(false);

  const { language } = useEnvironment();

  const {
    challengeData: { challenge } = { challenge: {} },
    challengeSections,
    challengeSteps,
    requestChallengeSections,
    requestChallengeSteps,
  } = useChallengeData();
  const isSPC = isSelfPaced(challenge);
  const isSections = isSPC && challenge?.stepsSummary?.sectionsNumber > 0;

  const firstPageStepsLimit = settings.get(challengeSettings.agendaStepsShown);
  const isFirstPageLimitReached = isLimitReached(
    challengeSteps,
    challengeSections,
    firstPageStepsLimit,
  );
  const challengeStepsWithLimit = getStepsWithLimit(
    opened,
    challengeSteps,
    firstPageStepsLimit,
  );
  const challengeSectionsWithLimit = getSectionsWithLimit(
    opened,
    challengeSections,
    firstPageStepsLimit,
    isFirstPageLimitReached,
  );

  const startDate =
    (isSpecific(challenge) &&
      challenge?.settings?.timelineType?.specific?.startTime) ||
    (isOngoing(challenge) &&
      challenge?.settings?.timelineType?.ongoing?.startTime);
  const stepsByStartDate = (locales) =>
    !isSPC && challengeStepsWithLimit?.length
      ? groupBy(
          toParticipantSteps({
            ownerSteps: { steps: challengeStepsWithLimit || [] },
            initialDate: startDate
              ? getRightDateFromBackendDateTime(startDate)
              : null,
            locale: locales[language],
          }),
          (step) => {
            return step?.dateFrame?.start;
          },
        )
      : null;

  React.useEffect(() => {
    if (challenge && isSections) {
      requestChallengeSections(challenge.id);
    } else if (challenge) {
      requestChallengeSteps(challenge.id);
    }
  }, []);

  return (
    <DateFnsLocales>
      {(locales) => (
        <div
          className={`${st(classes.root, {
            mobile: isMobile,
            alignment,
          })} ${className}`}
        >
          {isSPC && !isSections ? (
            <ChallengeAgendaStepsList steps={challengeStepsWithLimit || []} />
          ) : null}

          {isSPC && isSections ? (
            <>
              {(challengeSectionsWithLimit || []).map((section, i) => (
                <ChallengeAgendaSection
                  key={i}
                  title={section?.settings?.description?.title}
                  steps={section?.steps}
                  type="Accordion"
                />
              ))}
            </>
          ) : null}

          {!isSPC && stepsByStartDate(locales)
            ? Object.keys(stepsByStartDate(locales)).map((date, i) => {
                return (
                  <ChallengeAgendaSection
                    key={i}
                    title={format(getRightDateFromBackend(date), 'EEEE, PP', {
                      locale: locales[language],
                    })}
                    steps={stepsByStartDate(locales)[date]?.map(
                      (participantStep) =>
                        challengeSteps.find(
                          ({ id }) => id === participantStep.id,
                        ),
                    )}
                    type="Calendar"
                  />
                );
              })
            : null}

          {isFirstPageLimitReached && !opened ? (
            <button
              className={classes.link}
              onClick={() => {
                setOpened(true);
              }}
            >
              {settings.get(challengeSettings.agendaShowMoreText)}
            </button>
          ) : null}
        </div>
      )}
    </DateFnsLocales>
  );
};

ChallengeAgenda.displayName = 'ChallengeAgenda';
ChallengeAgenda.defaultProps = {
  className: '',
};
